import React, { useEffect, useMemo, useState } from "react";
import { Offcanvas, Table } from "react-bootstrap";
import { isHTMLIdentifier } from "../../../utils/universalFunction";
import { useParams } from "react-router";
import Swal from "sweetalert2";
import { studySiteDataEntryServices } from "../../../APIServices/studySiteDataEntryServices";

const MapperPreview = ({
  Show,
  Hide,
  Title,
  previewData,
  columns,
  sourceData = [],
  level
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [formFieldLoader, setFormFieldLoader] = useState(false);
  const [dataEntryMessage, setDataEntryMessage] = useState("");
  const { studyId } = useParams()
  const StudyMode = JSON.parse(localStorage.getItem("StudyMode"));

  React.useEffect(() => {
    setCurrentIndex(0);
  }, [previewData, sourceData]);

  const totalRows = sourceData?.length || 0;
  const currentSourceRow = sourceData?.[currentIndex] || {};

  const getSampleValue = (row) => {
    const mappedColumns = Array.isArray(row?.mappedColumns)
      ? row.mappedColumns.filter(Boolean)
      : [];

    const valuesFromSource = mappedColumns
      .map((column) => currentSourceRow?.[column] ?? '')
      .filter((value) => value !== undefined && value !== null && value !== '')
      .join(' / ');

    if (row?.kind === 'context') {
      if (valuesFromSource) {
        return valuesFromSource;
      }

      const fallbackValue = row?.sampleValue;
      const blockedContextValues = [
        'SITEID',
        'SUBJECTID',
        'EVENTID',
        'EVENTLABEL',
        'TIMEPOINTID',
        'TIMEPOINTUNIQUEID',
        'FORMID',
        'FORMVERSION',
        'FORMTYPE',
        'DATAENTRYTYPE',
        'EVENTREPEAT',
        'FORMREPEAT',
      ];

      const fallbackText = String(fallbackValue ?? '').trim();
      const fieldNameText = String(row?.fieldName ?? '').trim();
      const fieldLabelText = String(row?.fieldLabel ?? '').trim();

      if (fallbackText !== '') {
        const normalizedValue = fallbackText.toUpperCase();
        const normalizedFieldName = fieldNameText.toUpperCase();
        const normalizedFieldLabel = fieldLabelText.toUpperCase();

        if (
          normalizedValue !== normalizedFieldName &&
          normalizedValue !== normalizedFieldLabel &&
          !blockedContextValues.includes(normalizedValue)
        ) {
          return fallbackValue;
        }
      }

      return '_';
    }

    if (!mappedColumns.length) {
      return '-';
    }

    return valuesFromSource || '-';
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < Math.max(totalRows - 1, 0)) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const getMappedData = (data) => {
    const payload = {};

    (data || []).forEach((item) => {
      const mappedColumns = Array.isArray(item?.mappedColumns)
        ? item.mappedColumns.filter(Boolean)
        : [];

      if (item?.kind !== "context" && !mappedColumns.length) {
        return;
      }

      const resolvedValue = getSampleValue(item);
      const normalizedValue = String(resolvedValue ?? "").trim();
      if (!normalizedValue || normalizedValue === "_" || normalizedValue === "-") {
        return;
      }

      if (item?.kind === "context") {
        if (item?.fieldName) {
          payload[item.fieldName] = resolvedValue;
        }
        return;
      }

      if (item?.customField === "yes") {
        const mainFieldName = String(item?.parentFieldName || item?.fieldName || "").trim().toUpperCase();
        const currentFieldName = String(item?.fieldName || "").trim().toUpperCase();

        if (!mainFieldName || !currentFieldName) {
          return;
        }

        if (mainFieldName === currentFieldName) {
          payload[mainFieldName] = resolvedValue;
          return;
        }

        payload[`${mainFieldName}_${currentFieldName}`] = resolvedValue;
        return;
      }

      const mappedColumnKey = mappedColumns[0];
      const parentFieldName = String(item?.parentFieldName || "").trim().toUpperCase();
      const currentFieldName = String(item?.fieldName || "").trim().toUpperCase();

      if (parentFieldName && currentFieldName && parentFieldName !== currentFieldName) {
        payload[`${parentFieldName}_${currentFieldName}`] = resolvedValue;
        return;
      }

      payload[mappedColumnKey] = resolvedValue;
    });

    return payload;
  };

  const mappedPreviewData = useMemo(() => getMappedData(previewData), [previewData, currentIndex, sourceData]);

  const getMappedColumnTextStyle = (mappedColumns = []) => {
    const normalizedColumns = Array.isArray(mappedColumns)
      ? mappedColumns.map((item) => String(item || '').trim().toUpperCase()).filter(Boolean)
      : [];

    if (!normalizedColumns.length) {
      return { color: '#6C757D', fontWeight: 600 };
    }

    if (normalizedColumns.includes('DYNAMIC')) {
      return { color: '#198754', fontWeight: 600 };
    }

    if (normalizedColumns.includes('DEFAULT')) {
      return { color: '#291987', fontWeight: 600 };
    }

    return { color: '#FD7E14', fontWeight: 600 };
  };

  const checkEventLevelFormDataEntry = async (e) => {
    setFormFieldLoader(true)
    let userData = {}
    userData.studyId = studyId
    userData.siteId = mappedPreviewData?.SITEID
    userData.subjectId = mappedPreviewData?.SUBJECTID
    userData.eventId = mappedPreviewData?.EVENTID
    userData.timePointUniqueId = mappedPreviewData?.TIMEPOINTUNIQUEID || ""
    userData.formId = mappedPreviewData?.FORMID
    userData.formVersion = mappedPreviewData?.FORMVERSION
    userData.mode = StudyMode
    userData.level = "event"
    let data = await studySiteDataEntryServices.checkEventLevelFormDataEntry(userData);
    if (data?.count > 0) {
      setDataEntryMessage(
        `This form cannot be uploaded or used for data entry for the selected Site > Subject > Event${mappedPreviewData?.TIMEPOINTUNIQUEID ? " > Time Point ID" : ""
        }.`
      );
    } else {
      setDataEntryMessage("")
    }
    setFormFieldLoader(false)
  };

  useEffect(() => {
    if (!previewData?.length) return;
    checkEventLevelFormDataEntry();
  }, [mappedPreviewData, studyId, StudyMode, level]);

  const uploadFileSingleRowStudySubjectDataEntry = async (e) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to Save Data Entry!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, save it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setButtonLoader(true);
        let userData = {};
        userData.studyId = studyId;
        userData.rowObject = getMappedData(previewData);
        userData.mode = StudyMode;
        userData.level = level;
        let data = await studySiteDataEntryServices.uploadFileSingleRowStudySubjectDataEntry(
          userData
        );
        if (data?.statusCode === 200) {
          checkEventLevelFormDataEntry();
        }
      }
      setButtonLoader(false);
    });
  };

  const hasMappedField = (previewData || []).some(
    (row) =>
      row?.kind === "field" &&
      Array.isArray(row?.mappedColumns) &&
      row.mappedColumns.length > 0
  );

  return (
    <>
      <Offcanvas show={Show} onHide={Hide} placement="end" style={{ width: 1200 }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{Title}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <div style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  Preview Output
                </div>
                <strong style={{ fontSize: 13, color: '#16334A' }}>Preview Mapping</strong>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <span className="badge bg-success-subtle text-success border border-success-subtle" style={{ fontSize: 11 }}>
                  {previewData?.length || 0} mapped rows
                </span>
                <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: 11 }}>
                  {columns?.length || 0} columns
                </span>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex gap-2 align-items-center">
                <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
                  Row {Math.min(totalRows ? currentIndex + 1 : 0, totalRows)} / {totalRows}
                </span>
                <span className={`badge ${(dataEntryMessage && !formFieldLoader) ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-warning-subtle text-warning border border-warning-subtle'}`} style={{ fontSize: 11 }}>
                  {formFieldLoader ? "Checking..." : dataEntryMessage ? 'Completed' : 'Pending'}
                </span>
                <button style={{ fontSize: 10, padding: "0px 10px", marginTop: 0 }} className="hstack btn btn-sm btn-success"
                  disabled={!hasMappedField || dataEntryMessage || formFieldLoader || buttonLoader || !totalRows} 
                  onClick={uploadFileSingleRowStudySubjectDataEntry}>
                  {buttonLoader ? "Saving..." : "Save Data Entry"}
                </button>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <button style={{ fontSize: 11, padding: "0px 10px", marginTop: -2 }} className="btn btn-sm btn-outline-secondary" disabled={formFieldLoader || buttonLoader || currentIndex === 0} onClick={handlePrev}>
                  Previous
                </button>
                <button style={{ fontSize: 11, padding: "0px 10px", marginTop: -2 }} className="btn btn-sm btn-outline-secondary" disabled={formFieldLoader || buttonLoader || currentIndex >= Math.max(totalRows - 1, 0)} onClick={handleNext}>
                  Next
                </button>
              </div>
            </div>

            <div style={{ border: '1px solid #E9EEF4', borderRadius: '10px', overflow: 'hidden' }}>
              <Table striped bordered hover size="sm" responsive className="mb-0">
                <thead className="position-sticky top-0 z-1">
                  <tr style={{ background: '#EEF6FF' }}>
                    <th style={{ fontSize: 12, color: '#16334A' }}>Type</th>
                    <th style={{ fontSize: 12, color: '#16334A' }}>Form Field</th>
                    <th style={{ fontSize: 12, color: '#16334A' }}>Field Name</th>
                    <th style={{ fontSize: 12, color: '#16334A' }}>Mapped Column(s)</th>
                    <th style={{ fontSize: 12, color: '#16334A' }}>Sample Value</th>
                  </tr>
                </thead>
                <tbody>
                  {(previewData || []).map((row) => (
                    <tr key={row?.id}>
                      <td style={{ fontSize: 12 }}>
                        <span className={`badge ${row?.kind === 'context' ? 'bg-secondary-subtle text-secondary' : 'bg-info-subtle text-info'} border`} style={{ fontSize: 10 }}>
                          {row?.kind === 'context' ? 'Context' : 'Field'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {isHTMLIdentifier(row?.fieldLabel || "") ? (
                          <span dangerouslySetInnerHTML={{ __html: row?.fieldLabel }} />
                        ) : (
                          row?.fieldLabel
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: '#516779' }}>
                        {row?.fieldName}
                      </td>
                      <td style={{ fontSize: 12, ...getMappedColumnTextStyle(row?.mappedColumns) }}>
                        {row?.mappedColumns?.length ? row.mappedColumns.join(', ') : 'Not mapped'}
                      </td>
                      <td style={{ fontSize: 12 }}>{getSampleValue(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default MapperPreview;